// src/stations/scanner/adaptiveQuestGenerator.ts — Adaptive Remediation Generator (T-6.1.3, FR-SCAN-002)
// Generates exactly 5 marine-themed remediation drill questions targeting diagnosed KS2 weakness codes.

import type { DiagnosedMisconception, RemediationQuestion } from '@storage/scannerStorage';

export class AdaptiveQuestGenerator {
  /**
   * Generates a 5-question adaptive remediation quest based on diagnosed KS2 error codes.
   * Strictly guarantees exactly 5 questions (FR-SCAN-002).
   */
  static generate(misconceptions: DiagnosedMisconception[], scanId: string): RemediationQuestion[] {
    const questions: RemediationQuestion[] = [];
    const codes = misconceptions.map((m) => m.ks2Code);

    // Question Bank categorized by KS2 Objective Code
    const bank: Record<string, RemediationQuestion[]> = {
      // 4F2: Fractions & Common Denominators
      '4F2': [
        {
          id: `${scanId}_4f2_1`,
          ks2Code: '4F2',
          topic: 'Common Denominators',
          prompt: 'Clawey combines 1/4 of a vial of blue bioluminescent liquid with 1/2 of a vial of green liquid. What total fraction of a vial does he have?',
          contextText: 'Tip: Convert halves into quarters first so both portions have identical slice sizes!',
          options: ['2/6 of a vial', '3/4 of a vial', '2/4 of a vial', '1/8 of a vial'],
          correctOptionIndex: 1,
          explanation: '1/2 equals 2/4. Then 1/4 + 2/4 = 3/4. Do not add denominators!',
          socraticHint: 'How many quarters make a half? Convert 1/2 to fourths before adding the numerators.',
        },
        {
          id: `${scanId}_4f2_2`,
          ks2Code: '4F2',
          topic: 'Subtracting Fractions',
          prompt: 'A marine research tank was 5/6 full of sea water. After testing, 2/6 was siphoned into a specimen jar. How much water remains?',
          contextText: 'The denominators are already identical (sixths).',
          options: ['3/6 (or 1/2)', '3/0', '7/6', '3/12'],
          correctOptionIndex: 0,
          explanation: 'Since the denominators match, subtract the numerators: 5/6 - 2/6 = 3/6 = 1/2.',
          socraticHint: 'The slice size is sixths throughout. Just subtract 5 - 2 for the remaining slices.',
        },
        {
          id: `${scanId}_4f2_3`,
          ks2Code: '4F2',
          topic: 'Equivalent Denominators',
          prompt: 'Tudor shares a sea-sponge cake. Tudor eats 1/3 and Clawey eats 1/6. What fraction did they eat altogether?',
          contextText: 'Convert 1/3 to sixths first.',
          options: ['2/9', '3/6 (or 1/2)', '2/6', '1/9'],
          correctOptionIndex: 1,
          explanation: '1/3 = 2/6. Then 2/6 + 1/6 = 3/6 = 1/2. They ate half the cake.',
          socraticHint: 'Multiply top and bottom of 1/3 by 2 to get sixths. Now add 2/6 + 1/6!',
        },
        {
          id: `${scanId}_4f2_4`,
          ks2Code: '4F2',
          topic: 'Fraction Word Problem',
          prompt: 'A benthic expedition submarine uses 3/8 of its battery on descent and 2/8 on sonar mapping. How much battery remains for the return ascent?',
          contextText: 'Total capacity is 8/8.',
          options: ['5/8 used, 3/8 remains', '5/16 used, 11/16 remains', '1/8 remains', '6/8 remains'],
          correctOptionIndex: 0,
          explanation: '3/8 + 2/8 = 5/8 used. Subtract from the full whole: 8/8 - 5/8 = 3/8 remains.',
          socraticHint: 'Add the two amounts used first: 3/8 + 2/8. How many eighths are left to make 8/8?',
        },
        {
          id: `${scanId}_4f2_5`,
          ks2Code: '4F2',
          topic: 'Adding Unlike Fractions',
          prompt: 'Which addition shows the correct way to add 2/5 and 3/10?',
          contextText: 'Both fractions must share the same denominator.',
          options: ['2/5 + 3/10 = 5/15', '4/10 + 3/10 = 7/10', '2/10 + 3/10 = 5/10', '5/10 = 1/2'],
          correctOptionIndex: 1,
          explanation: 'Convert 2/5 into 4/10 (by multiplying numerator and denominator by 2). Then 4/10 + 3/10 = 7/10.',
          socraticHint: 'Double the top and bottom of 2/5 to change it into tenths before adding.',
        },
      ],

      // 5G4: Angles & Protractor Measurement
      '5G4': [
        {
          id: `${scanId}_5g4_1`,
          ks2Code: '5G4',
          topic: 'Acute Angles',
          prompt: 'Clawey opens his claw to an angle that looks smaller than a square corner (90°). Which measurement could it be?',
          contextText: 'An acute angle is strictly between 0° and 90°.',
          options: ['135°', '52°', '95°', '180°'],
          correctOptionIndex: 1,
          explanation: '52° is between 0° and 90°, making it an acute angle. 135° is obtuse.',
          socraticHint: 'A square book corner is 90°. Acute means sharp and smaller than 90°.',
        },
        {
          id: `${scanId}_5g4_2`,
          ks2Code: '5G4',
          topic: 'Protractor Scale Reading',
          prompt: 'A student places a 180° protractor on an angle. One arm points at 0° on the inner scale and the other arm points to 40° inner / 140° outer. What is the angle size?',
          contextText: 'Always follow the scale that starts at 0° on your base baseline!',
          options: ['140°', '40°', '180°', '90°'],
          correctOptionIndex: 1,
          explanation: 'Because the baseline starts at 0° on the inner scale, read from the inner scale: 40° (acute).',
          socraticHint: 'Look at the baseline: does it start at 0° on the inside or outside? Follow that same track.',
        },
        {
          id: `${scanId}_5g4_3`,
          ks2Code: '5G4',
          topic: 'Obtuse Angle Recognition',
          prompt: 'The rudder on the naturalist schooner rotates past a right angle to 125°. How is this angle classified?',
          options: ['Acute angle', 'Obtuse angle', 'Reflex angle', 'Right angle'],
          correctOptionIndex: 1,
          explanation: 'Angles between 90° and 180° are obtuse.',
          socraticHint: 'Is 125° greater than 90° but less than a straight line (180°)?',
        },
        {
          id: `${scanId}_5g4_4`,
          ks2Code: '5G4',
          topic: 'Angles on a Straight Line',
          prompt: 'Two angles meet along a straight line seabed reef. One angle measures 110°. What is the size of the other angle?',
          contextText: 'Angles on a straight line always sum to 180°.',
          options: ['90°', '70°', '80°', '250°'],
          correctOptionIndex: 1,
          explanation: '180° - 110° = 70°. Together they make a straight line.',
          socraticHint: 'What number added to 110° makes 180°?',
        },
        {
          id: `${scanId}_5g4_5`,
          ks2Code: '5G4',
          topic: 'Reflex Angles',
          prompt: 'Which angle is a reflex angle (greater than 180°)?',
          options: ['89°', '175°', '215°', '90°'],
          correctOptionIndex: 2,
          explanation: 'Reflex angles are greater than 180° and less than 360°. 215° is reflex.',
          socraticHint: 'A straight line is 180°. A reflex angle bends around past the straight line.',
        },
      ],

      // 4E1: SPAG & Homophones & Fronted Adverbials
      '4E1': [
        {
          id: `${scanId}_4e1_1`,
          ks2Code: '4E1',
          topic: 'Homophones: They\'re / Their / There',
          prompt: 'Choose the correct word to complete the sentence: "The marine divers left _____ underwater cameras inside the submarine."',
          options: ['there', 'their', 'they\'re', 'thier'],
          correctOptionIndex: 1,
          explanation: 'Their shows possession (the cameras belonging to the divers).',
          socraticHint: 'Does the sentence talk about something the divers own? Their with an "i" shows ownership.',
        },
        {
          id: `${scanId}_4e1_2`,
          ks2Code: '4E1',
          topic: 'Fronted Adverbial Punctuation',
          prompt: 'Which sentence punctuates the fronted adverbial correctly?',
          options: [
            'Beneath the calm water, Tudor spotted an ancient ammonite.',
            'Beneath the calm water Tudor, spotted an ancient ammonite.',
            'Beneath the calm, water Tudor spotted an ancient ammonite.',
            'Beneath, the calm water Tudor spotted an ancient ammonite.',
          ],
          correctOptionIndex: 0,
          explanation: 'A comma must immediately follow the introductory adverbial phrase ("Beneath the calm water,").',
          socraticHint: 'Read the sentence aloud. Where does the introductory phrase end before the main action begins?',
        },
        {
          id: `${scanId}_4e1_3`,
          ks2Code: '4E1',
          topic: 'Contraction Check: It\'s vs Its',
          prompt: 'Choose the correct word: "The giant crab snapped _____ powerful claw in warning."',
          options: ['it\'s', 'its', 'its\'', 'it was'],
          correctOptionIndex: 1,
          explanation: 'Its (no apostrophe) is the possessive form. It\'s means "it is".',
          socraticHint: 'Test saying "it is". "Snapped it is claw" makes no sense, so use possessive "its".',
        },
        {
          id: `${scanId}_4e1_4`,
          ks2Code: '4E1',
          topic: 'Time Fronted Adverbials',
          prompt: 'Identify the fronted adverbial in: "Early the next morning, the crew set sail for the Outer Hebrides."',
          options: ['the crew', 'Early the next morning', 'set sail', 'Outer Hebrides'],
          correctOptionIndex: 1,
          explanation: '"Early the next morning" is a fronted adverbial of time describing when the action occurred.',
          socraticHint: 'Which opening phrase tells you WHEN the action happened?',
        },
        {
          id: `${scanId}_4e1_5`,
          ks2Code: '4E1',
          topic: 'Homophones: Passed vs Past',
          prompt: 'Select the correct sentence:',
          options: [
            'Clawey swam passed the sunken shipwreck.',
            'Clawey swam past the sunken shipwreck.',
            'In the passed, divers used heavy iron helmets.',
            'The submarine past all speed trials yesterday.',
          ],
          correctOptionIndex: 1,
          explanation: '"Past" is a preposition describing movement beyond a landmark. "Passed" is the past tense of the verb pass.',
          socraticHint: '"Past" describes moving by a place ("swam past the reef").',
        },
      ],

      // 4M7: Perimeter & Area
      '4M7': [
        {
          id: `${scanId}_4m7_1`,
          ks2Code: '4M7',
          topic: 'Perimeter vs Area',
          prompt: 'An aquarium pool is 8 metres long and 5 metres wide. What is the PERIMETER of the walkway around it?',
          contextText: 'Perimeter is the total boundary distance around all 4 sides.',
          options: ['40 metres', '26 metres', '13 metres', '80 metres'],
          correctOptionIndex: 1,
          explanation: 'Perimeter = 8 + 5 + 8 + 5 = 26 metres. (40 m² is the area!).',
          socraticHint: 'Add all four sides: length + width + length + width.',
        },
        {
          id: `${scanId}_4m7_2`,
          ks2Code: '4M7',
          topic: 'Area Calculation',
          prompt: 'What is the surface AREA of the same pool (8m long by 5m wide)?',
          options: ['26 m²', '40 m²', '13 m²', '80 m²'],
          correctOptionIndex: 1,
          explanation: 'Area = length × width = 8 × 5 = 40 m².',
          socraticHint: 'Area multiplies length by width to count the square tiles.',
        },
        {
          id: `${scanId}_4m7_3`,
          ks2Code: '4M7',
          topic: 'Perimeter of a Square',
          prompt: 'A square lobster cage has a perimeter of 36 cm. What is the length of one side?',
          options: ['9 cm', '6 cm', '18 cm', '12 cm'],
          correctOptionIndex: 0,
          explanation: 'All 4 sides of a square are equal: 36 ÷ 4 = 9 cm.',
          socraticHint: 'Divide the total perimeter 36 by the 4 equal sides.',
        },
        {
          id: `${scanId}_4m7_4`,
          ks2Code: '4M7',
          topic: 'Composite Rectilinear Area',
          prompt: 'An L-shaped coral tank is split into two rectangles: 4cm × 3cm and 2cm × 2cm. What is the total area?',
          options: ['12 cm²', '16 cm²', '20 cm²', '14 cm²'],
          correctOptionIndex: 1,
          explanation: 'Area 1 = 4 × 3 = 12 cm². Area 2 = 2 × 2 = 4 cm². Total = 12 + 4 = 16 cm².',
          socraticHint: 'Calculate the area of each rectangle separately, then add them together.',
        },
        {
          id: `${scanId}_4m7_5`,
          ks2Code: '4M7',
          topic: 'Perimeter of a Regular Hexagon',
          prompt: 'A hexagonal tide pool has 6 equal sides, each measuring 7 metres. What is its perimeter?',
          options: ['42 metres', '36 metres', '49 metres', '13 metres'],
          correctOptionIndex: 0,
          explanation: '6 sides × 7 metres = 42 metres.',
          socraticHint: 'Multiply the number of sides (6) by the length of each side (7m).',
        },
      ],

      // 4N4: Multi-Step Word Problems (Fallback / Default)
      '4N4': [
        {
          id: `${scanId}_4n4_1`,
          ks2Code: '4N4',
          topic: 'Multi-Step Subtraction & Division',
          prompt: 'Tudor collects 60 sea urchin fossils. He keeps 12 for his study and shares the rest equally between 6 display trays. How many fossils go on each tray?',
          options: ['8 fossils', '10 fossils', '12 fossils', '6 fossils'],
          correctOptionIndex: 0,
          explanation: 'First step: 60 - 12 = 48 fossils remaining. Second step: 48 ÷ 6 = 8 fossils per tray.',
          socraticHint: 'Subtract the 12 fossils he kept first. Then divide the remainder by 6.',
        },
        {
          id: `${scanId}_4n4_2`,
          ks2Code: '4N4',
          topic: 'Multi-Step Currency',
          prompt: 'A student buys 3 specimen jars at £2.50 each and pays with a £10 note. What change should they receive?',
          options: ['£7.50', '£2.50', '£3.50', '£1.50'],
          correctOptionIndex: 1,
          explanation: '3 × £2.50 = £7.50. Change from £10 is £10.00 - £7.50 = £2.50.',
          socraticHint: 'Calculate the total cost of the 3 jars first (3 × £2.50), then subtract from £10.',
        },
        {
          id: `${scanId}_4n4_3`,
          ks2Code: '4N4',
          topic: 'Time Multi-Step Problem',
          prompt: 'A bathysphere dive lasts 45 minutes, followed by a 20-minute rest, and a second 35-minute dive. How long did the whole expedition take?',
          options: ['1 hour 40 minutes', '1 hour 20 minutes', '100 minutes', '1 hour 30 minutes'],
          correctOptionIndex: 0,
          explanation: '45 + 20 + 35 = 100 minutes. 100 minutes = 1 hour 40 minutes.',
          socraticHint: 'Add all three durations: 45 + 20 + 35 = 100 mins. How many whole hours is 100 mins?',
        },
        {
          id: `${scanId}_4n4_4`,
          ks2Code: '4N4',
          topic: 'Scaling & Multiplication',
          prompt: 'A lobster swims 15 metres in 1 minute. How far will it travel in 6 minutes at the same steady pace?',
          options: ['75 metres', '90 metres', '60 metres', '100 metres'],
          correctOptionIndex: 1,
          explanation: '15 metres × 6 minutes = 90 metres.',
          socraticHint: 'Multiply 15 by 6. (Hint: 15 × 2 = 30; 30 × 3 = 90).',
        },
        {
          id: `${scanId}_4n4_5`,
          ks2Code: '4N4',
          topic: 'Remainder Word Problem',
          prompt: '38 sea shells are packed into boxes of 6. How many full boxes can be filled, and how many shells are left over?',
          options: ['6 boxes and 2 left over', '5 boxes and 8 left over', '7 boxes and 0 left over', '6 boxes and 4 left over'],
          correctOptionIndex: 0,
          explanation: '38 ÷ 6 = 6 with a remainder of 2 (since 6 × 6 = 36, and 38 - 36 = 2).',
          socraticHint: 'What is the closest multiple of 6 under 38? 6 × 6 = 36.',
        },
      ],
    };

    // Pick questions matching the diagnosed codes first
    const defaultPool = bank['4N4'] ?? [];
    const primaryCode = codes[0] || '4N4';
    const primaryPool = bank[primaryCode] ?? defaultPool;

    if (codes.length === 1) {
      // Exactly 5 from this domain
      questions.push(...primaryPool.slice(0, 5));
    } else {
      // Distribute across multiple diagnosed codes
      let codeIdx = 0;
      let qIdx = 0;
      while (questions.length < 5) {
        const c = codes[codeIdx % codes.length] || '4N4';
        const list = bank[c] ?? defaultPool;
        const q = list[qIdx % list.length];
        if (q && !questions.some((existing) => existing.prompt === q.prompt)) {
          questions.push(q);
        } else if (q) {
          // Clone with new id to avoid collisions
          questions.push({ ...q, id: `${q.id}_dup_${questions.length}` });
        }
        codeIdx += 1;
        if (codeIdx % codes.length === 0) qIdx += 1;
      }
    }

    // Strict guarantee: exactly 5 questions
    return questions.slice(0, 5);
  }
}
